import React from 'react';
import Inputs from './inputs';
import InputMaps from './InputMaps';
import logger from '../utils/logger';

interface SubField {
  name: string;
  type: string;
  value: string;
  isRequired?: boolean;
  options?: string[];
  isShowConditionally?: boolean;
  showWhen?: {
    field: string;
    operator: string;
    value: any;
  };
}

interface GroupFieldProps {
  groupName: string;
  groupValue: string;
  subFields: SubField[];
  groupData: any; // The actual data for this group (e.g., leadData[groupValue])
  onChange: (groupValue: string, fieldName: string, value: any, coords?: any) => void;
  errors?: { [key: string]: string };
  disabled?: boolean;
}

/**
 * GroupField Component
 * Renders a group of related fields with optional conditional visibility
 *
 * Features:
 * - Renders group header with blue card styling
 * - Supports nested subFields with conditional visibility
 * - Handles all field types (text, selection, number, quantity, etc.)
 * - Integrates with parent form validation
 */
const GroupField: React.FC<GroupFieldProps> = ({
  groupName,
  groupValue,
  subFields,
  groupData = {},
  onChange,
  errors = {},
  disabled = false,
}) => {

  /**
   * Check if a field should be visible based on conditional logic
   * @param subField - The field to check
   * @returns boolean - Whether the field should be displayed
   */
  const shouldShowField = (subField: SubField): boolean => {
    // If field is not conditional, always show it
    if (!subField.isShowConditionally || !subField.showWhen) {
      return true;
    }

    const { field, operator, value: expectedValue } = subField.showWhen;
    const actualValue = groupData?.[field];

    logger.log(`🔍 Checking conditional visibility for ${subField.name}:`, {
      field,
      operator,
      expectedValue,
      actualValue,
      groupData
    });

    // Handle different operators
    switch (operator) {
      case 'equals':
        return actualValue === expectedValue;
      case 'notEquals':
        return actualValue !== expectedValue;
      case 'contains':
        if (Array.isArray(actualValue)) {
          return actualValue.includes(expectedValue);
        }
        return String(actualValue || '').includes(expectedValue);
      case 'notContains':
        if (Array.isArray(actualValue)) {
          return !actualValue.includes(expectedValue);
        }
        return !String(actualValue || '').includes(expectedValue);
      case 'greaterThan':
        return Number(actualValue) > Number(expectedValue);
      case 'lessThan':
        return Number(actualValue) < Number(expectedValue);
      default:
        logger.log(`⚠️ Unknown operator: ${operator}`);
        return true;
    }
  };

  /**
   * Handle change event for subfields
   */
  const handleSubFieldChange = (fieldName: string, value: any, coords?: any) => {
    logger.log(`📝 Group field change: ${groupValue}.${fieldName} =`, value);
    onChange(groupValue, fieldName, value, coords);
  };

  /**
   * Get the full field path for error lookup
   */
  const getErrorPath = (subFieldValue: string): string => {
    return `${groupValue}.${subFieldValue}`;
  };

  return (
    <div className="group-field-container" style={{
      background: '#f8fafc',
      border: '2px solid #cbd5e1',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Group Header */}
      <div className="group-header" style={{
        paddingBottom: '14px',
        marginBottom: '18px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          borderRadius: '8px',
          padding: '6px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px'
        }}>
          <span style={{ fontSize: '1.1rem' }}>📋</span>
        </div>
        <h4 style={{
          margin: 0,
          color: '#1e293b',
          fontSize: '1.05rem',
          fontWeight: '600',
          letterSpacing: '-0.01em'
        }}>
          {groupName}
        </h4>
      </div>

      {/* SubFields */}
      <div className="group-fields" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '14px'
      }}>
        {subFields.map((subField, index) => {
          // Check conditional visibility
          if (!shouldShowField(subField)) {
            logger.log(`⏭️ Hiding conditional field: ${subField.name}`);
            return null;
          }

          const fieldValue = groupData?.[subField.value];
          const errorPath = getErrorPath(subField.value);
          const fieldError = errors[errorPath];

          logger.log(`🎯 Rendering subfield: ${subField.name}`, {
            type: subField.type,
            value: fieldValue,
            hasError: !!fieldError
          });

          // Handle different field types
          if (subField.type === 'location') {
            return (
              <div key={index} className="form-group">
                <InputMaps
                  disabled={disabled}
                  type={subField.type}
                  name={subField.value}
                  value={fieldValue?.address || ''}
                  locationData={fieldValue}
                  onChange={(name: string, value: string, coords: any) =>
                    handleSubFieldChange(name, value, coords)
                  }
                  className="form-control"
                  label={subField.name}
                  isRequired={subField.isRequired || false}
                />
                {fieldError && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                    {fieldError}
                  </div>
                )}
              </div>
            );
          }

          if (subField.type === 'date') {
            return (
              <div key={index} className="form-group">
                <label className="form-label">
                  {subField.name}
                  {subField.isRequired && <span style={{ color: 'red' }}>*</span>}
                </label>
                <input
                  disabled={disabled}
                  type="date"
                  name={subField.value}
                  className="form-control"
                  value={fieldValue || ''}
                  onChange={(e: any) =>
                    handleSubFieldChange(subField.value, e.target.value)
                  }
                  placeholder="Enter Date"
                />
                {fieldError && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                    {fieldError}
                  </div>
                )}
              </div>
            );
          }

          // Default: use Inputs component for all other types
          return (
            <div key={index} className="form-group">
              <Inputs
                disabled={disabled}
                name={subField.value}
                type={
                  subField.type === 'phone' || subField.type === 'amount'
                    ? 'number'
                    : subField.type  // Keep 'quantity' as is for stepper rendering
                }
                autoComplete="off"
                className="form-control"
                placeholder={`Enter ${subField.name}`}
                value={fieldValue || ''}
                onChange={(e: any) =>
                  handleSubFieldChange(subField.value, e.target.value)
                }
                label={subField.name}
                isRequired={subField.isRequired || false}
                options={
                  ['selection', 'multi-selection'].includes(subField.type)
                    ? subField.options
                    : undefined
                }
                id=""
              />
              {fieldError && (
                <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                  {fieldError}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupField;
